import { ClassNamesFromIStyles, useClassNames } from '@h2oai/ui-kit';
import { ChartData } from 'chart.js';

import { lineChartOptions } from '../../../common/utils';
import BarChart from '../../BarChart/BarChart';
import LineChart from '../../LineChart/LineChart';
import PieChart from '../../PieChart/PieChart';
import { StatCard } from '../../StatCard/StatCard';
import GPTeInfoCard from '../GPTeInfoCard/GPTeInfoCard';
import GPTePlot from '../GPTePlot/GPTePlot';
import { mapActionName } from '../H2OGPTe.utils';
import {
  useDailyTokenUsageData,
  useGPTeStats,
  useGPTeTokenStats,
  useLLMTokenUsageData,
} from './GPTeTokenData.services';
import { gpteTokenDataStylesDefault, IGPTeTokenDataStyles } from './GPTeTokenData.styles';

interface IGPTeTOkenDataProps {
  startDate: Date;
  endDate: Date;
}

const GPTeTokenData = ({ startDate, endDate }: IGPTeTOkenDataProps) => {
  const classNames = useClassNames<IGPTeTokenDataStyles, ClassNamesFromIStyles<IGPTeTokenDataStyles>>(
    'gpte-token-data',
    gpteTokenDataStylesDefault
  );

  const {
    inputTokens,
    outputTokens,
    tokensPerMessage,
    mostUsedLLM,
    llmTokenUsage,
    llmUsageCount,
    tokenStatsLoading,
    tokenStatsError,
  } = useGPTeTokenStats(startDate, endDate);

  const { dailyTokenUsageChartData, dailyTokenUsageLoading, dailyTokenUsageError } = useDailyTokenUsageData(
    startDate,
    endDate
  );

  const { llmTokenUsageChartData, llmTokenUsageLoading, llmTokenUsageError } = useLLMTokenUsageData(startDate, endDate);

  const { chartsData, statsLoading, statsError } = useGPTeStats(startDate, endDate);

  const getPieChartOptions = (title: string) => {
    return {
      responsive: true,
      plugins: {
        legend: {
          position: 'bottom' as const,
        },
        title: {
          display: true,
          text: title,
          font: {
            size: 16,
            weight: 'bold' as const,
          },
        },
      },
    };
  };

  const barChartOptions = {
    responsive: true,
    plugins: {
      legend: {
        position: 'bottom' as const,
      },
      title: {
        display: true,
        text: 'Token Usage by LLMs',
        font: {
          size: 16,
          weight: 'bold' as const,
        },
      },
    },
    scales: {
      x: { stacked: true },
      y: {
        stacked: true,
        min: 0,
        beginAtZero: true,
      },
    },
  };

  return (
    <div className={classNames.root}>
      <div className={classNames.gridAreaContainer}>
        <StatCard
          title="Token Usage"
          mainLabel={'Total Token Usage'}
          mainValue={inputTokens + outputTokens}
          subStats={[
            { label: 'Input Tokens', value: inputTokens },
            { label: 'Output Tokens', value: outputTokens },
            { label: 'Tokens per Message', value: tokensPerMessage },
          ]}
          statsLoading={tokenStatsLoading}
          statsError={!!tokenStatsError}
        />
        <StatCard
          title="Token Usage by LLMs"
          mainLabel={'Most Used LLM'}
          mainValue={mostUsedLLM}
          subStats={[
            { label: 'Total Tokens', value: llmTokenUsage },
            { label: 'Message Count', value: llmUsageCount },
          ]}
          statsLoading={tokenStatsLoading}
          statsError={!!tokenStatsError}
        />

        <LineChart
          data={dailyTokenUsageChartData || null}
          options={lineChartOptions(
            'Total Token Usage',
            dailyTokenUsageChartData && dailyTokenUsageChartData.labels?.length
              ? dailyTokenUsageChartData.labels?.length
              : 0,
            16
          )}
          loading={dailyTokenUsageLoading}
          error={!!dailyTokenUsageError}
        />
        <BarChart
          data={llmTokenUsageChartData}
          options={barChartOptions}
          loading={llmTokenUsageLoading}
          error={!!llmTokenUsageError}
        />

        <div className={classNames.pieChartWrapper}>
          <PieChart
            data={chartsData.llmUsageData as ChartData<'pie'>}
            options={getPieChartOptions('LLM Usage')}
            maxSlices={4}
            loading={statsLoading}
            error={!!statsError}
          />
        </div>
        <div className={classNames.pieChartWrapper}>
          <PieChart
            data={chartsData.llmDescriptionData as ChartData<'pie'>}
            options={getPieChartOptions('LLM Descriptions')}
            maxSlices={4}
            loading={statsLoading}
            error={!!statsError}
          />
        </div>
        <GPTePlot
          title="API Called"
          yAxisLabel="Usage Count"
          data={chartsData.apiCalledData as ChartData<'bar'>}
          tableColumnHeader={'Method'}
          loading={statsLoading}
          error={!!statsError}
        />
        <GPTePlot
          title="RPC Called"
          yAxisLabel="Usage Count"
          data={chartsData.rpcCalledData as ChartData<'bar'>}
          tableColumnHeader={'Method'}
          loading={statsLoading}
          error={!!statsError}
        />
        <GPTePlot
          title="Chat Actions"
          yAxisLabel="Number of Events"
          data={chartsData.chatData as ChartData<'bar'>}
          tableColumnHeader={'Action'}
          loading={statsLoading}
          error={!!statsError}
        />
        <GPTePlot
          title="Collection Actions"
          yAxisLabel="Number of Events"
          data={chartsData.collectionsData as ChartData<'bar'>}
          tableColumnHeader={'Action'}
          loading={statsLoading}
          error={!!statsError}
        />
        <GPTeInfoCard
          title={'Job Actions'}
          data={Object.entries(chartsData.jobData).map(([key, value]) => ({
            label: mapActionName(key),
            value: Number(value),
          }))}
        />
        <GPTeInfoCard
          title={'Document Actions'}
          data={Object.entries(chartsData.documentsData).map(([key, value]) => ({
            label: mapActionName(key),
            value: Number(value),
          }))}
        />
      </div>
    </div>
  );
};

export default GPTeTokenData;
